import { Component } from "react";
import { withTranslation } from "react-i18next";
import produce from "immer";
import environment from "src/utils/environment";
import { compose } from "recompose";
import debug from "src/modules/debug-logger";
import {
  getMonitorings,
  deleteActivityLog,
  getSnapshotSignedUrl,
  getVideoSignedUrl,
  deleteActivityLogEvent,
  getDataForMediaUrl,
  downloadEventMedia,
  enableMonitoringSharing,
  disableMonitoringSharing
} from "src/modules/activity-log/ajax/cloud";
import { withPurchase } from "src/modules/purchase/PurchaseContext";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import Dialog from "src/components/Dialog";
import CapturedMedia from "src/modules/activity-log/components/CapturedMedia";
import { isPhotoEvent, isVideoEvent } from "src/modules/activity-log/supported-events";
import { Monitoring } from "src/modules/activity-log/monitoring-info";
import { blobDataCache } from "src/modules/activity-log/BlobDataCache";

import decodePbData from "./decodePbData";
import NoMonitoringsView from "./NoMonitoringsView";
import LoadingMonitoringsErrorView from "./LoadingMonitoringsErrorView/LoadingMonitoringsErrorView";
import LoadingView from "./LoadingView";
import MonitoringsView from "./MonitoringsView";
import { connect } from "react-redux";
import FirstNoMonitoringsView from "./FirstNoMonitoringsView/FirstNoMonitoringsView";

const FETCH_LIMIT = 20;
const { isBrowser } = environment;

class HistoryActivityLog extends Component {
  state = {
    hasLoadingError: false,
    loading: true,
    hasGenericFailure: false,
    isDeleting: false,
    openedEvent: null,
    isOpenDeleteMonitoringDialog: false
  };

  componentDidMount() {
    const { monitorings } = this.props.flowState;
    if (Object.keys(monitorings).length === 0) {
      this.loadMoreMonitorings();
    } else {
      this.setState({ loading: false });
    }
  }

  componentWillUnmount() {
    blobDataCache.revokeUrls();
  }

  getEventForId = (eventId, monitoringId) => {
    const { monitorings, selectedMonitoringId } = this.props.flowState;
    const monitoring = monitorings[monitoringId || selectedMonitoringId];
    return monitoring?.data?.events[eventId];
  };

  loadMoreMonitorings = async () => {
    if (this.state.hasLoadingError) {
      this.setState({ loading: true, hasLoadingError: false });
    }
    try {
      const { monitorings } = this.props.flowState;
      const newMonitorings = await getMonitorings({
        skip: Object.keys(monitorings).length,
        limit: FETCH_LIMIT
      });
      const newMonitoringsAsObject = newMonitorings.reduce((monitoringsObject, monitoring) => {
        monitoringsObject[monitoring.uniqueId] = monitoring;
        return monitoringsObject;
      }, {});
      this.props.setFlowState({
        monitorings: {
          ...this.props.flowState.monitorings,
          ...newMonitoringsAsObject
        },
        hasMoreMonitorings: newMonitorings.length === FETCH_LIMIT
      });
      if (this.state.loading && newMonitorings[0]) {
        this.handleMonitoringSelection(newMonitorings[0].uniqueId);
      }
      this.setState({
        loading: false
      });
    } catch (error) {
      debug.activityLog("loadMoreMonitorings failed: ", error);
      this.setState({ hasLoadingError: true, loading: false });
    }
  };

  handleMonitoringSelection = (monitoringId) => {
    let hadDecodedData;

    const produceState = (draftState) => {
      draftState.selectedMonitoringId = monitoringId;
      const monitoring = draftState.monitorings[monitoringId];
      hadDecodedData = Boolean(monitoring.data);
      if (!hadDecodedData) {
        monitoring.data = decodePbData(monitoring.pbData);
      }
    };

    this.props.setFlowState(produce(produceState));
  };

  fetchEventThumbnail = async (event) => {
    const { selectedMonitoringId, monitorings } = this.props.flowState;
    const monitoringStartDate = monitorings[selectedMonitoringId]?.startDate;

    const isMovingPreviewAvailable =
      monitoringStartDate && event.eventType.includes("VIDEO") && new Date(monitoringStartDate) > new Date("1.1.2023");
    const fileExtension = isMovingPreviewAvailable ? "webp" : "jpg";

    try {
      const dataUrl = await blobDataCache.get(event, selectedMonitoringId, fileExtension);

      const produceState = (draftState) => {
        const currentMonitoring = draftState.monitorings[selectedMonitoringId];
        if (currentMonitoring) {
          currentMonitoring.data.events[event.id].thumbnailSrc = dataUrl;
          currentMonitoring.data.events[event.id].fileExtension = fileExtension;
        }
      };
      this.props.setFlowState(produce(produceState));
    } catch (error) {
      debug.activityLog("fetchEventThumbnail failed", error);
    }
  };

  openEventView = async (eventId) => {
    const { selectedMonitoringId } = this.props.flowState;
    const event = this.getEventForId(eventId);

    this.setState({
      openedEvent: isBrowser && event.signedUrl ? event : { id: eventId }
    });

    if (isBrowser && event.signedUrl) return;
    if (event.mediaDataUrl) return;
    try {
      if (isPhotoEvent(event.eventType)) {
        const signedUrl = await getSnapshotSignedUrl(event.fileName.value);
        await this.cacheMediaAsDataUrl({
          eventId,
          monitoringId: selectedMonitoringId,
          url: signedUrl
        });
        return;
      }

      if (isVideoEvent(event.eventType)) {
        const signedUrl = await getVideoSignedUrl(event.fileName.value);
        if (
          this.props.flowState.selectedMonitoringId !== selectedMonitoringId ||
          this.state.openedEvent?.id !== eventId
        ) {
          return;
        }
        this.setState(({ openedEvent }) => ({
          openedEvent: { ...openedEvent, signedUrl }
        }));

        this.cacheMediaAsDataUrl({
          eventId,
          monitoringId: selectedMonitoringId,
          url: signedUrl
        });
        return;
      }
    } catch (error) {
      debug.activityLog(error);
      this.setState({ hasGenericFailure: true, openedEvent: null });
    }
  };

  cacheMediaAsDataUrl = async ({ monitoringId, eventId, url }) => {
    const event = this.getEventForId(eventId, monitoringId);
    if (!event) return;
    try {
      const data = await getDataForMediaUrl(url);
      this.props.setFlowState(
        produce((draftState) => {
          const currentMonitoring = draftState.monitorings[monitoringId];
          const currentEvent = currentMonitoring.data.events[eventId];
          if (currentMonitoring && currentEvent) {
            if (isBrowser) currentEvent.signedUrl = url;
            currentEvent.mediaDataUrl = data.dataUrl;
            currentEvent.mediaBlob = data.blob;
          }
        })
      );
    } catch (error) {
      debug.activityLog("cacheMediaAsDataUrl failed:", error);
    }
  };

  handleMonitoringDelete = async () => {
    const { flowState } = this.props;
    const { monitorings, selectedMonitoringId } = flowState;
    const monitoringToDelete = monitorings[selectedMonitoringId];
    const monitoringsArray = Object.values(monitorings);
    const selectedMonitoringIndex = monitoringsArray.findIndex((monitoring) => monitoring === monitoringToDelete);
    try {
      this.setState({ isDeleting: true, isOpenDeleteMonitoringDialog: false });

      await deleteActivityLog(monitoringToDelete.objectId);

      const newSelectedMonitoringId =
        selectedMonitoringIndex === 0
          ? monitoringsArray[1].uniqueId
          : monitoringsArray[selectedMonitoringIndex - 1].uniqueId;
      this.handleMonitoringSelection(newSelectedMonitoringId);

      this.props.setFlowState(
        produce((draftState) => {
          delete draftState.monitorings[selectedMonitoringId];
        })
      );
      this.setState({ isDeleting: false });
    } catch (error) {
      this.setState({ isDeleting: false, hasGenericFailure: true });
    }
  };

  handleEventDelete = async (eventId) => {
    const { selectedMonitoringId, monitorings } = this.props.flowState;
    const selectedMonitoring = monitorings[selectedMonitoringId];
    const event = this.getEventForId(eventId);

    try {
      await deleteActivityLogEvent({
        activityLogId: selectedMonitoring.objectId,
        eventId
      });
      URL.revokeObjectURL(event.thumbnailSrc);
      URL.revokeObjectURL(event.mediaDataUrl);
      this.setState({ openedEvent: null }, () => {
        this.props.setFlowState(
          produce((draftState) => {
            const currentMonitoring = draftState.monitorings[selectedMonitoringId];
            delete currentMonitoring.data.events[eventId];
          })
        );
      });
    } catch (error) {
      debug.activityLog("handleEventDelete failed: ", error);
      this.setState({
        openedEvent: null,
        hasGenericFailure: true
      });
    }
  };

  handleEventMediaDownload = async (eventId) => {
    try {
      const { flowState } = this.props;
      const { monitorings, selectedMonitoringId } = flowState;
      const selectedMonitoring = monitorings[selectedMonitoringId];
      const event = this.getEventForId(eventId);
      await downloadEventMedia({
        eventType: event.eventType,
        fileName: event.fileName.value,
        mediaBlob: event.mediaBlob,
        captureDate: new Date(selectedMonitoring.startDate).getTime() + event.start
      });
    } catch (error) {
      debug.activityLog("handleEventMediaDownload failed: ", error);
    }
  };

  toggleShare = async () => {
    const { selectedMonitoringId, monitorings } = this.props.flowState;
    const selectedMonitoring = monitorings[selectedMonitoringId];

    const isShared = Monitoring.isShared(selectedMonitoring);
    const result = isShared
      ? await disableMonitoringSharing(selectedMonitoring.objectId)
      : await enableMonitoringSharing(selectedMonitoring.objectId);
    const { shareableKey, shareableUntil } = result || {};

    this.props.setFlowState(
      produce((draftState) => {
        draftState.monitorings[selectedMonitoringId] = {
          ...selectedMonitoring,
          shareableKey,
          shareableUntil
        };
      })
    );
  };

  render() {
    const { loading, hasLoadingError, hasGenericFailure, isDeleting, openedEvent, isOpenDeleteMonitoringDialog } =
      this.state;
    const { onClose, openDescription, flowState, purchase, showFirstEmptyScreen, t } = this.props;
    const { monitorings, selectedMonitoringId, hasMoreMonitorings } = flowState;
    const selectedMonitoring = monitorings[selectedMonitoringId];
    const originalOpenedEvent = openedEvent ? this.getEventForId(openedEvent.id) : null;

    if (hasLoadingError) {
      return <LoadingMonitoringsErrorView onClose={onClose} onRetry={this.loadMoreMonitorings} />;
    }
    if (loading) {
      return <LoadingView onClose={onClose} />;
    }
    if (Object.keys(monitorings).length === 0) {
      return showFirstEmptyScreen ? (
        <FirstNoMonitoringsView onClose={onClose} openDescription={openDescription} />
      ) : (
        <NoMonitoringsView onClose={onClose} openDescription={openDescription} />
      );
    }
    return (
      <>
        {selectedMonitoring && (
          <MonitoringsView
            onClose={onClose}
            hasMoreMonitorings={hasMoreMonitorings}
            monitorings={monitorings}
            openedEvent={openedEvent}
            selectedMonitoring={selectedMonitoring}
            openDescription={openDescription}
            onMonitoringDelete={() => this.setState({ isOpenDeleteMonitoringDialog: true })}
            loadMoreMonitorings={this.loadMoreMonitorings}
            handleMonitoringSelection={this.handleMonitoringSelection}
            openEventView={this.openEventView}
            toggleFreemiumFlow={() => purchase.open({ content: "soundsAndVideos" })}
            fetchEventThumbnail={this.fetchEventThumbnail}
            toggleShare={this.toggleShare}
          />
        )}
        {openedEvent && (
          <CapturedMedia
            isOpen={Boolean(openedEvent)}
            onClose={() => this.setState({ openedEvent: null })}
            src={openedEvent.signedUrl || originalOpenedEvent.mediaDataUrl}
            ratio={originalOpenedEvent.mediaRatio}
            type={originalOpenedEvent.eventType}
            videoThumbnailSrc={originalOpenedEvent.thumbnailSrc}
            onDelete={() => this.handleEventDelete(openedEvent.id)}
            onDownload={() => this.handleEventMediaDownload(openedEvent.id)}
            autoplay={true}
          />
        )}
        <GenericFailureDialog
          isOpen={hasGenericFailure}
          onDismiss={() => this.setState({ hasGenericFailure: false })}
        />
        <Dialog
          isOpen={isOpenDeleteMonitoringDialog || isDeleting}
          isProcessing={isDeleting}
          processingText={t("deleting")}
          heading={t("activityLog.deleteDialog.heading")}
          text={t("activityLog.deleteDialog.text")}
          buttons={[
            {
              text: t("buttons.cancel"),
              onClick: () => this.setState({ isOpenDeleteMonitoringDialog: false })
            },
            {
              text: t("buttons.ok"),
              onClick: this.handleMonitoringDelete
            }
          ]}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  showFirstEmptyScreen: !state.app.hasSeenMonitoringHelp
});

const enhance = compose(connect(mapStateToProps), withTranslation(), withPurchase);

export default enhance(HistoryActivityLog);
